"use client";

import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ComponentRef, useRef } from "react";

import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";
import { IconInput } from "ui/core/input";

import { useAddTradingViewIndex } from "../services/add-trading-view-account";
import { useTradingViewAccount } from "../services/get-trading-view-account";

export const TradingViewAccountForm = () => {
  const { t } = useLang();
  const inputRef = useRef<ComponentRef<typeof IconInput>>(null);

  const { profile } = useAuth();
  const isBusinessUser = profile?.authProvider === "Business";

  const { mutate, isLoading: isSubmitting } = useAddTradingViewIndex();

  const { data, isError } = useTradingViewAccount();
  const hasSubmitted = Boolean(data);

  const sendToServer = ({ accountId }: { accountId: string }) => {
    mutate(
      { tradingviewAccount: accountId },
      {
        onSuccess: () => {
          if (!inputRef.current) return;
          inputRef.current.value = "";
        },
      }
    );
  };

  const onButtonClick = () => {
    if (!inputRef.current) return;

    const accountId = inputRef.current.value;
    if (accountId) sendToServer({ accountId });
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="w-full @lg:w-auto">
        <IconInput
          ref={inputRef}
          placeholder={data ? data : t.analysis.trend.tradingViewIndex.account}
          className="py-2 text-sm @lg:min-w-[320px]"
          leadingIcon={
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          }
          disabled={isBusinessUser || isSubmitting || isError}
        />
      </div>
      <Button
        className="w-full py-2 @lg:w-fit"
        variant="secondary"
        isLoading={isSubmitting}
        disabled={isBusinessUser}
        onClick={onButtonClick}
      >
        {hasSubmitted
          ? t.analysis.trend.tradingViewIndex.edit
          : t.analysis.trend.tradingViewIndex.submit}
      </Button>
    </div>
  );
};
